import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Image, Spinner, Table } from 'react-bootstrap';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import SearchBar from '../../../components/SearchBar';
import { TherapistListDto } from '../../../../../shared/dtos/therapist.dto';
import TherapistDetailsModal from './TherapistDetailsModal';
import { formatDateTime } from '../../../util/date-utils';

function buildQueryParameters(searchTerm: string): URLSearchParams {
  const queryParameters: URLSearchParams = new URLSearchParams();
  const trimmedSearchTerm: string = searchTerm.trim();

  if (trimmedSearchTerm) {
    queryParameters.set('searchTerm', trimmedSearchTerm);
  }

  return queryParameters;
}

function getTherapistName(therapist: TherapistListDto): string {
  const fullName: string = `${therapist.firstName} ${therapist.lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return therapist.email;
}

const Therapists = (): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [therapists, setTherapists] = useState<TherapistListDto[]>([]);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTherapistId, setSelectedTherapistId] = useState<string | null>(null);

  const fetchTherapists: (searchTerm: string) => Promise<void> = useCallback(async (searchTerm: string): Promise<void> => {
    setIsLoading(true);
    setHasError(false);

    try {
      const response: TherapistListDto[] = await authFetch('/api/v1/admin/therapists', {
        method: 'GET',
        queryParameters: buildQueryParameters(searchTerm),
      });

      setTherapists(response ?? []);
    } catch (error) {
      console.error(error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  },
  [authFetch],
  );

  useEffect(() => {
    void fetchTherapists(appliedSearchTerm);
  }, [appliedSearchTerm, fetchTherapists]);

  const handleSearch: (value: string) => void = useCallback((value: string): void => {
    setAppliedSearchTerm(value.trim());
  }, []);

  function handleOpenDetailsModal(therapist: TherapistListDto): void {
    setSelectedTherapistId(therapist.id);
    setShowDetailsModal(true);
  }

  function handleCloseDetailsModal(): void {
    setShowDetailsModal(false);
    setSelectedTherapistId(null);
  }

  function renderTableBody(): ReactElement {
    if (isLoading) {
      return (
        <tr>
          <td
            className="text-center py-4"
            colSpan={7}>
            <Spinner
              animation="border"
              size="sm"
            />
          </td>
        </tr>
      );
    }

    if (therapists.length === 0) {
      return (
        <tr>
          <td
            className="text-center text-muted py-4"
            colSpan={7}>
            {i18next.t('therapists.empty')}
          </td>
        </tr>
      );
    }

    return (
      <>
        {therapists.map((therapist: TherapistListDto) => (
          <tr key={therapist.id}>
            <td>
              <div className="d-flex align-items-center gap-3">
                <Image
                  src={therapist.profilePic ?? '../default_profile.png'}
                  roundedCircle
                  className="border flex-shrink-0"
                  width={48}
                  height={48}
                  alt={getTherapistName(therapist)}
                />

                <div>
                  <div className="fw-semibold">{getTherapistName(therapist)}</div>
                  <div className="text-muted small">{therapist.email}</div>
                </div>
              </div>
            </td>

            <td className="text-center">{therapist.plannedAppointmentsCount}</td>
            <td className="text-center">{therapist.doneAppointmentsCount}</td>
            <td className="text-center">{therapist.sessionsCount}</td>
            <td className="text-center">{therapist.patientsCount}</td>
            <td className="text-nowrap">{formatDateTime(therapist.lastLoginAt)}</td>

            <td className="text-end">
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  handleOpenDetailsModal(therapist);
                }}>
                {i18next.t('therapists.table.details')}
              </Button>
            </td>
          </tr>
        ))}
      </>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <SearchBar
            onSearch={handleSearch}
            debounceMs={300}
            maxWidth={undefined}
          />
        </div>
      </div>

      {hasError && (
        <Alert
          variant="danger"
          className="mb-0">
          {i18next.t('therapists.error')}
        </Alert>
      )}

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <Table
            striped
            bordered
            hover
            className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>{i18next.t('therapists.table.therapist')}</th>

                <th className="text-center">
                  {i18next.t('therapists.table.plannedAppointments')}
                </th>

                <th className="text-center">
                  {i18next.t('therapists.table.doneAppointments')}
                </th>

                <th className="text-center">
                  {i18next.t('therapists.table.sessions')}
                </th>

                <th className="text-center">
                  {i18next.t('therapists.table.patients')}
                </th>

                <th>{i18next.t('therapists.table.lastLoginAt')}</th>

                <th className="text-end" />
              </tr>
            </thead>

            <tbody>{renderTableBody()}</tbody>
          </Table>
        </div>
      </div>

      {selectedTherapistId && (
        <TherapistDetailsModal
          show={showDetailsModal}
          therapistId={selectedTherapistId}
          onClose={handleCloseDetailsModal}
        />
      )}
    </div>
  );
};

export default Therapists;